import { useState, useEffect, useMemo } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { emitTourEvent } from "@/lib/tour-events";
import {
  useListCustomers,
  useListEstimates,
  useCreateCustomer,
  useBulkDeleteCustomers,
  getListCustomersQueryKey,
} from "@workspace/api-client-react";
import { usePageTitle } from "@/hooks/use-page-title";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { Search, Plus, Mail, Phone, MapPin, Loader2, Building2, Trash2, ChevronDown, ChevronUp, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { BulkDeleteDialog } from "@/components/bulk-delete-dialog";

const customerSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  addressLine1: z.string().optional().or(z.literal("")),
  addressLine2: z.string().optional().or(z.literal("")),
  city: z.string().optional().or(z.literal("")),
  state: z.string().optional().or(z.literal("")),
  postalCode: z.string().optional().or(z.literal("")),
  country: z.string().optional().or(z.literal("")),
  shippingAddressLine1: z.string().optional().or(z.literal("")),
  shippingAddressLine2: z.string().optional().or(z.literal("")),
  shippingCity: z.string().optional().or(z.literal("")),
  shippingState: z.string().optional().or(z.literal("")),
  shippingPostalCode: z.string().optional().or(z.literal("")),
  shippingCountry: z.string().optional().or(z.literal("")),
  paymentTermsDays: z.union([z.coerce.number().int().min(0), z.literal("")]).optional(),
  notes: z.string().optional().or(z.literal("")),
  taxExempt: z.boolean().optional(),
});

function formatAddress(c: {
  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  postalCode?: string | null;
  country?: string | null;
}): string {
  return [
    c.addressLine1,
    c.addressLine2,
    [c.city, c.state].filter(Boolean).join(", "),
    c.postalCode,
    c.country,
  ].filter(Boolean).join(", ");
}

export default function Customers() {
  usePageTitle("Customers");
  const [search, setSearch] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [diffShipping, setDiffShipping] = useState(false);
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  useEffect(() => {
    if (new URLSearchParams(searchString).get("new") === "1") {
      setIsCreateOpen(true);
      setLocation("/customers");
    }
  }, [searchString, setLocation]);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const debouncedSearch = useDebouncedValue(search);
  const { data: customers, isLoading } = useListCustomers({ search: debouncedSearch || undefined });
  const { data: allEstimates } = useListEstimates();
  const createCustomer = useCreateCustomer();

  const customerStats = useMemo(() => {
    const map = new Map<number, { count: number; totalValue: number; acceptedValue: number }>();
    for (const est of allEstimates ?? []) {
      if (est.customerId == null) continue;
      const existing = map.get(est.customerId) ?? { count: 0, totalValue: 0, acceptedValue: 0 };
      existing.count += 1;
      existing.totalValue += est.total;
      if (est.status === "accepted" || est.status === "converted") {
        existing.acceptedValue += est.total;
      }
      map.set(est.customerId, existing);
    }
    return map;
  }, [allEstimates]);
  const bulkDeleteCustomers = useBulkDeleteCustomers();

  const form = useForm<z.infer<typeof customerSchema>>({
    resolver: zodResolver(customerSchema),
    defaultValues: {
      name: "", email: "", phone: "",
      addressLine1: "", addressLine2: "", city: "", state: "", postalCode: "", country: "",
      shippingAddressLine1: "", shippingAddressLine2: "", shippingCity: "", shippingState: "", shippingPostalCode: "", shippingCountry: "",
      paymentTermsDays: "", notes: "", taxExempt: false,
    },
  });

  const onSubmit = (data: z.infer<typeof customerSchema>) => {
    const payload = {
      ...data,
      addressLine1: data.addressLine1 || undefined,
      addressLine2: data.addressLine2 || undefined,
      city: data.city || undefined,
      state: data.state || undefined,
      postalCode: data.postalCode || undefined,
      country: data.country || undefined,
      shippingAddressLine1: diffShipping ? (data.shippingAddressLine1 || undefined) : undefined,
      shippingAddressLine2: diffShipping ? (data.shippingAddressLine2 || undefined) : undefined,
      shippingCity: diffShipping ? (data.shippingCity || undefined) : undefined,
      shippingState: diffShipping ? (data.shippingState || undefined) : undefined,
      shippingPostalCode: diffShipping ? (data.shippingPostalCode || undefined) : undefined,
      shippingCountry: diffShipping ? (data.shippingCountry || undefined) : undefined,
      paymentTermsDays: typeof data.paymentTermsDays === "number" ? data.paymentTermsDays : undefined,
      notes: data.notes || undefined,
    };
    createCustomer.mutate({ data: payload }, {
      onSuccess: (newCustomer) => {
        queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
        setIsCreateOpen(false);
        form.reset();
        emitTourEvent("customer.created");
        toast({
          title: "Customer created",
          description: `${newCustomer.name} has been added.`,
        });
      },
      onError: () => {
        toast({
          title: "Error",
          description: "Failed to create customer.",
          variant: "destructive",
        });
      }
    });
  };

  const allIds = customers?.map(c => c.id) ?? [];
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.has(id));
  const someSelected = allIds.some(id => selectedIds.has(id)) && !allSelected;

  const toggleSelectAll = () => {
    if (allSelected || someSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(allIds));
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const confirmBulkDelete = () => {
    const ids = Array.from(selectedIds);
    bulkDeleteCustomers.mutate({ data: { ids } }, {
      onSuccess: (result) => {
        queryClient.invalidateQueries({ queryKey: getListCustomersQueryKey() });
        setSelectedIds(new Set());
        setBulkDeleteOpen(false);
        toast({ title: `${result.deleted} customer(s) deleted` });
      },
      onError: (err: unknown) => {
        setBulkDeleteOpen(false);
        const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error
          ?? "Failed to delete selected customers";
        toast({ title: "Could not delete", description: message, variant: "destructive" });
      },
    });
  };

  const selectedCount = selectedIds.size;

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-8 h-8 text-primary" />
            Customers
          </h1>
          <p className="text-muted-foreground mt-1">Manage your clients and their contact information.</p>
        </div>

        <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="w-4 h-4 mr-2" />
              New Customer
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Customer</DialogTitle>
              <DialogDescription>
                Create a new customer profile to start making quotes for them.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Customer Name / Company</FormLabel>
                      <FormControl>
                        <Input placeholder="Acme Corp or Jane Doe" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Email</FormLabel>
                        <FormControl>
                          <Input type="email" placeholder="jane@example.com" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Phone</FormLabel>
                        <FormControl>
                          <Input type="tel" placeholder="(555) 123-4567" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Billing Address</p>
                  <FormField control={form.control} name="addressLine1" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="Address line 1" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <FormField control={form.control} name="addressLine2" render={({ field }) => (
                    <FormItem><FormControl><Input placeholder="Address line 2 (optional)" {...field} /></FormControl><FormMessage /></FormItem>
                  )} />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="city" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="City" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="state" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="State" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField control={form.control} name="postalCode" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="ZIP / Postal code" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="country" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="Country" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                  </div>
                </div>

                <div className="flex items-center gap-2 py-1">
                  <Switch checked={diffShipping} onCheckedChange={setDiffShipping} id="diff-shipping-create" />
                  <label htmlFor="diff-shipping-create" className="text-sm cursor-pointer">Different shipping address</label>
                </div>

                {diffShipping && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Shipping Address</p>
                    <FormField control={form.control} name="shippingAddressLine1" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="Address line 1" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <FormField control={form.control} name="shippingAddressLine2" render={({ field }) => (
                      <FormItem><FormControl><Input placeholder="Address line 2 (optional)" {...field} /></FormControl><FormMessage /></FormItem>
                    )} />
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="shippingCity" render={({ field }) => (
                        <FormItem><FormControl><Input placeholder="City" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="shippingState" render={({ field }) => (
                        <FormItem><FormControl><Input placeholder="State" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <FormField control={form.control} name="shippingPostalCode" render={({ field }) => (
                        <FormItem><FormControl><Input placeholder="ZIP / Postal code" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                      <FormField control={form.control} name="shippingCountry" render={({ field }) => (
                        <FormItem><FormControl><Input placeholder="Country" {...field} /></FormControl><FormMessage /></FormItem>
                      )} />
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    control={form.control}
                    name="paymentTermsDays"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Payment Terms (days)</FormLabel>
                        <FormControl>
                          <Input type="number" min="0" placeholder="e.g. 30" {...field} value={field.value ?? ""} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="taxExempt"
                    render={({ field }) => (
                      <FormItem className="flex flex-col justify-end pb-1">
                        <FormLabel>Tax Exempt</FormLabel>
                        <div className="flex items-center gap-2 mt-1">
                          <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                          <span className="text-sm text-muted-foreground">{field.value ? "Yes" : "No"}</span>
                        </div>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="notes"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notes</FormLabel>
                      <FormControl>
                        <Textarea placeholder="Internal notes about this customer..." className="min-h-[60px] resize-none" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <DialogFooter className="pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={createCustomer.isPending}>
                    {createCustomer.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Save Customer
                  </Button>
                </DialogFooter>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input 
            placeholder="Search customers by name..." 
            className="pl-10 bg-card"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {customers && customers.length > 0 && (
          <div className="flex items-center gap-2">
            <Checkbox
              checked={allSelected ? true : someSelected ? "indeterminate" : false}
              onCheckedChange={toggleSelectAll}
              aria-label="Select all customers"
            />
            <span className="text-sm text-muted-foreground">Select all</span>
          </div>
        )}
      </div>

      {/* Bulk action bar */}
      {selectedCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-destructive/10 border border-destructive/20 rounded-lg">
          <span className="text-sm font-medium text-destructive">{selectedCount} selected</span>
          <Button
            size="sm"
            variant="destructive"
            className="gap-2 h-8"
            onClick={() => setBulkDeleteOpen(true)}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Delete selected ({selectedCount})
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-8"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </Button>
        </div>
      )}

      {isLoading ? (
        <div className="grid gap-3">
          {[1, 2, 3, 4].map(i => (
            <Card key={i}>
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-9 h-9 bg-muted animate-pulse rounded-lg shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 bg-muted animate-pulse rounded" />
                  <div className="h-3 w-1/2 bg-muted animate-pulse rounded" />
                </div>
                <div className="flex gap-6">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="text-center space-y-1">
                      <div className="h-3 w-12 bg-muted animate-pulse rounded" />
                      <div className="h-4 w-10 bg-muted animate-pulse rounded mx-auto" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : customers?.length === 0 ? (
        <div className="text-center py-12 bg-card rounded-lg border border-dashed">
          <Building2 className="mx-auto h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">No customers found</h3>
          <p className="text-muted-foreground mt-1 max-w-sm mx-auto">
            {search ? "No customers matched your search query." : "Get started by adding your first customer."}
          </p>
          {!search && (
            <Button onClick={() => setIsCreateOpen(true)} className="mt-4" variant="outline">
              <Plus className="w-4 h-4 mr-2" />
              Add Customer
            </Button>
          )}
        </div>
      ) : (
        <div className="grid gap-3">
          {customers?.map((customer) => {
            const isSelected = selectedIds.has(customer.id);
            const stats = customerStats.get(customer.id) ?? { count: 0, totalValue: 0, acceptedValue: 0 };
            const address = formatAddress(customer);
            return (
              <Card
                key={customer.id}
                className={cn(
                  "hover:border-primary/60 hover:shadow-lg transition-all cursor-pointer group",
                  isSelected && "ring-2 ring-primary/50 border-primary/30"
                )}
                onClick={() => setLocation(`/customers/${customer.id}`)}
              >
                <CardContent className="p-4 flex items-center gap-4">
                  {/* Checkbox */}
                  <div
                    className="shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggleSelect(customer.id)}
                      aria-label={`Select ${customer.name}`}
                    />
                  </div>

                  {/* Icon pill */}
                  <div className="p-2 rounded-lg bg-muted shrink-0">
                    <Building2 className="w-5 h-5 text-muted-foreground" />
                  </div>

                  {/* Name + contact */}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-base truncate group-hover:text-primary transition-colors">
                      {customer.name}
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
                      {customer.phone && (
                        <span className="flex items-center gap-1 shrink-0">
                          <Phone className="w-3 h-3" />
                          {customer.phone}
                        </span>
                      )}
                      {address && (
                        <span className="flex items-center gap-1 truncate">
                          <MapPin className="w-3 h-3 shrink-0" />
                          <span className="truncate">{address}</span>
                        </span>
                      )}
                      {!customer.phone && !address && (
                        <span className="italic">No contact info</span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center gap-6 shrink-0 text-right">
                    <div>
                      <div className="text-xs text-muted-foreground">Quotes</div>
                      <div className="font-semibold text-sm" style={{ color: "#6366f1" }}>{stats.count}</div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Total Value</div>
                      <div className="font-semibold text-sm" style={{ color: "#3b82f6" }}>
                        {stats.totalValue.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-muted-foreground">Accepted</div>
                      <div className="font-semibold text-sm" style={{ color: "#22c55e" }}>
                        {stats.acceptedValue.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Bulk Delete Confirm */}
      <BulkDeleteDialog
        open={bulkDeleteOpen}
        onOpenChange={setBulkDeleteOpen}
        count={selectedCount}
        itemLabel="customer"
        onConfirm={confirmBulkDelete}
        isPending={bulkDeleteCustomers.isPending}
      />
    </div>
  );
}
